import { create } from 'zustand'
import { fetchMe, serverLogout, type Me } from '../api/auth'
import { refreshAccessToken, setOnAuthLost, tokens } from '../api/client'
import { useUI } from './ui'

type Status = 'loading' | 'anon' | 'authed'

type SessionState = {
  status: Status
  me: Me | null
  /** Restore a session on boot from the stored refresh token. */
  boot: () => Promise<void>
  /** Call after OTP verify has installed tokens — loads /auth/me. */
  onAuthenticated: () => Promise<void>
  /** Reload the profile (e.g. after a self password change). */
  reload: () => Promise<void>
  logout: () => void
  /** Become anon locally because another tab logged out (via the cross-tab storage signal). */
  externalLogout: () => void
}

export const useSession = create<SessionState>((set, get) => ({
  status: 'loading',
  me: null,
  boot: async () => {
    setOnAuthLost(() => {
      const wasAuthed = get().status === 'authed'
      tokens.clear()
      set({ status: 'anon', me: null })
      if (wasAuthed) useUI.getState().showToast('Session expired — please sign in again', 'info')
    })
    if (!tokens.getRefresh()) {
      set({ status: 'anon' })
      return
    }
    let outcome = await refreshAccessToken()
    if (outcome === 'transient') {
      await new Promise((r) => setTimeout(r, 1000))
      outcome = await refreshAccessToken()
    }
    if (outcome !== 'ok') {
      if (outcome === 'rejected') tokens.clear()
      set({ status: 'anon' })
      return
    }
    try {
      set({ status: 'authed', me: await fetchMe() })
    } catch {
      set({ status: 'anon', me: null })
    }
  },
  onAuthenticated: async () => {
    try {
      set({ status: 'authed', me: await fetchMe() })
    } catch {
      set({ status: 'anon', me: null })
    }
  },
  reload: async () => {
    try {
      set({ me: await fetchMe() })
    } catch {
      /* keep current */
    }
  },
  logout: () => {
    void serverLogout()
    tokens.clear()
    set({ status: 'anon', me: null })
  },
  externalLogout: () => {
    const wasAuthed = get().status === 'authed'
    tokens.clear() // clears this tab's in-memory access + bumps authEpoch (fences an in-flight refresh); refresh already removed by the other tab
    set({ status: 'anon', me: null })
    if (wasAuthed) useUI.getState().showToast('Signed out in another tab', 'info')
  },
}))
